<script setup>
import { computed, inject, onMounted, reactive } from 'vue'
import { RotateCcw, Search } from '@lucide/vue'
import { Badge, Button, EmptyState, Input, PageHeader, Pagination, Section, Select, Spinner, Table, Toolbar } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { LEDGER_REASONS, LEDGER_REF_TYPES, LEDGER_SOURCES, PAID_ACTIONS, paidActionRefundReason } from '@drama/contracts/platform'
import { useCursorPagination } from '../../composables/useCursorPagination.js'
import { formatDateTime, formatInt } from '../llm/llm-format.js'
import { formatSignedPoints, isoToLocalDateTime, ledgerTypeVariant, localDateTimeToIso } from './format.js'
import { ledgerReasonLabel, ledgerRefTypeLabel, ledgerSourceLabel } from './ledger-labels.js'

const PAGE_SIZES = [20, 50, 100]
const REASONS = [...PAID_ACTIONS.flatMap(action => [action, paidActionRefundReason(action)]), ...LEDGER_REASONS]

const api = inject('api')
if (!api) throw new Error('BillingLedger: api provider is required')
const { t } = useI18n()
const route = useRoute()

const filters = reactive({ user_id: '', reason: '', source: '', ref_type: '', ref_id: '', since: '', until: '', limit: PAGE_SIZES[0] })
const page = useCursorPagination(cursor => api.commerce.listAdminLedger(queryFor(cursor)))

const columns = computed(() => [
  { key: 'created_at', label: t('visualGameAdmin.billing.ledger.table.time'), width: '170px', mono: true },
  { key: 'user_id', label: t('visualGameAdmin.billing.ledger.table.user'), width: '260px', mono: true },
  { key: 'type', label: t('visualGameAdmin.billing.ledger.table.type'), width: '90px' },
  { key: 'amount', label: t('visualGameAdmin.billing.ledger.table.amount'), width: '110px', align: 'right', mono: true },
  { key: 'balance_after', label: t('visualGameAdmin.billing.ledger.table.balance'), width: '120px', align: 'right', mono: true },
  { key: 'reason', label: t('visualGameAdmin.billing.ledger.table.reason'), width: '180px' },
  { key: 'source', label: t('visualGameAdmin.billing.ledger.table.source'), width: '130px' },
  { key: 'reference', label: t('visualGameAdmin.billing.ledger.table.reference'), width: '320px', mono: true },
  { key: 'note', label: t('visualGameAdmin.billing.ledger.table.note'), width: '240px' },
])
// 看板深链可能带来名单外的值（本地测试数据），也放进选项，不然下拉框显示空白。
function filterOptions(values, current, allLabel, labelOf) {
  const all = current === '' || values.includes(current) ? values : [...values, current]
  return [{ value: '', label: allLabel }, ...all.map(value => ({ value, label: labelOf(t, value) }))]
}
const reasonOptions = computed(() => filterOptions(REASONS, filters.reason, t('visualGameAdmin.billing.ledger.filters.allReasons'), ledgerReasonLabel))
const sourceOptions = computed(() => filterOptions(LEDGER_SOURCES, filters.source, t('visualGameAdmin.billing.ledger.filters.allSources'), ledgerSourceLabel))
const refTypeOptions = computed(() => filterOptions(LEDGER_REF_TYPES, filters.ref_type, t('visualGameAdmin.billing.ledger.filters.allRefTypes'), ledgerRefTypeLabel))
const summary = computed(() => t('visualGameAdmin.billing.pager.summary', { page: page.pageNumber.value, n: page.items.value.length }))

onMounted(() => {
  // 订单页、看板会带着筛选条件深链过来，落地就按它筛。
  for (const field of ['user_id', 'reason', 'source', 'ref_type', 'ref_id']) filters[field] = queryText(route.query[field])
  filters.since = queryDateTime(route.query.since)
  filters.until = queryDateTime(route.query.until)
  page.reset()
})

function queryText(value) {
  return typeof value === 'string' ? value : ''
}

function queryDateTime(value) {
  return typeof value === 'string' ? isoToLocalDateTime(value) : ''
}

function queryFor(cursor) {
  const query = { limit: filters.limit }
  for (const field of ['user_id', 'reason', 'source', 'ref_type', 'ref_id']) {
    if (filters[field].trim() !== '') query[field] = filters[field].trim()
  }
  const since = localDateTimeToIso(filters.since)
  const until = localDateTimeToIso(filters.until)
  if (since !== null) query.since = since
  if (until !== null) query.until = until
  if (cursor !== null) query.cursor = cursor
  return query
}

function resetFilters() {
  for (const field of ['user_id', 'reason', 'source', 'ref_type', 'ref_id', 'since', 'until']) filters[field] = ''
  page.reset()
}

function changePageSize(size) {
  filters.limit = size
  page.reset()
}

function referenceText(row) {
  if (row.ref_type === null) return '—'
  const refType = ledgerRefTypeLabel(t, row.ref_type)
  return row.ref_id === null ? refType : `${refType} · ${row.ref_id}`
}
</script>

<template>
  <div class="vga-page" data-insp-path="apps/visual-game-admin/src/pages/billing/LedgerPage.vue">
    <PageHeader :title="t('visualGameAdmin.billing.ledger.title')" />

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.billing.ledger.filters.userId') }}</label>
          <Input v-model="filters.user_id" size="sm" @keyup.enter="page.reset" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.ledger.filters.reason') }}</label>
          <Select v-model="filters.reason" :options="reasonOptions" size="sm" block />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.ledger.filters.source') }}</label>
          <Select v-model="filters.source" :options="sourceOptions" size="sm" block />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.ledger.filters.refType') }}</label>
          <Select v-model="filters.ref_type" :options="refTypeOptions" size="sm" block />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.billing.ledger.filters.refId') }}</label>
          <Input v-model="filters.ref_id" size="sm" @keyup.enter="page.reset" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.ledger.filters.since') }}</label>
          <Input v-model="filters.since" type="datetime-local" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.ledger.filters.until') }}</label>
          <Input v-model="filters.until" type="datetime-local" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="page.loading.value" @click="page.reset">
            <Search :size="14" />{{ t('visualGameAdmin.waitlist.filters.search') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="page.loading.value" @click="resetFilters">
            <RotateCcw :size="14" />{{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <Section :padded="false">
      <div v-if="page.loading.value && page.items.value.length === 0" class="vga-state"><Spinner /></div>
      <div v-else-if="page.error.value" class="vga-state">
        <p class="vga-error">{{ page.error.value }}</p>
        <Button variant="secondary" @click="page.reload">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <EmptyState v-else-if="page.items.value.length === 0" :title="t('visualGameAdmin.billing.ledger.empty')">
        <template #action>
          <Button variant="secondary" @click="resetFilters">{{ t('visualGameAdmin.common.reset') }}</Button>
        </template>
      </EmptyState>
      <Table v-else :columns="columns" :rows="page.items.value">
        <template #created_at="{ row }">{{ formatDateTime(row.created_at) }}</template>
        <template #user_id="{ row }">
          <RouterLink class="vga-link" :to="`/users/${row.user_id}`">{{ row.user_id }}</RouterLink>
        </template>
        <template #type="{ row }">
          <Badge :variant="ledgerTypeVariant(row.type)">{{ t(`visualGameAdmin.billing.ledger.type.${row.type}`) }}</Badge>
        </template>
        <template #amount="{ row }">{{ formatSignedPoints(row.amount) }}</template>
        <template #balance_after="{ row }">{{ formatInt(row.balance_after) }}</template>
        <template #reason="{ row }">
          <span :title="row.reason">{{ ledgerReasonLabel(t, row.reason) }}</span>
          <span v-if="row.failure_code !== null" class="vga-error vga-mono ledger-failure">{{ row.failure_code }}</span>
        </template>
        <template #source="{ row }"><span :title="row.source">{{ ledgerSourceLabel(t, row.source) }}</span></template>
        <template #reference="{ row }">
          <RouterLink v-if="row.reference !== null" class="vga-link" :to="row.reference.href">{{ referenceText(row) }}</RouterLink>
          <span v-else>{{ referenceText(row) }}</span>
        </template>
        <template #note="{ row }">{{ row.note === null ? '—' : row.note }}</template>
      </Table>
    </Section>

    <Pagination
      :has-prev="page.hasPrev.value"
      :has-next="page.hasNext.value"
      :loading="page.loading.value"
      :prev-label="t('visualGameAdmin.billing.pager.prev')"
      :next-label="t('visualGameAdmin.billing.pager.next')"
      :summary="summary"
      :page-size-label="t('visualGameAdmin.billing.pager.pageSize')"
      :page-size="filters.limit"
      :page-size-options="PAGE_SIZES"
      @prev="page.previous"
      @next="page.next"
      @update:page-size="changePageSize"
    />
  </div>
</template>

<style scoped>
.ledger-failure {
  margin-left: var(--drama-space-xs);
  font-size: var(--drama-font-size-xs);
}
</style>
